import { EngineResult } from './constants'
import { decision } from './decision'

// ============================================================
// YEARLY RECURRENCE EVALUATOR
// ============================================================
//
// Supports:
//
// 1. Specific month and day
//
//      Example:
//      month_of_year = 5
//      day_of_month = 25
//
//      → May 25 every year
//
// 2. Multi-year cadence
//
//      Example:
//      interval_value = 2
//
//      → May 25 every 2 years
//
// The recurrence is anchored to the year of
// recurrence_start_date.
//
// IMPORTANT:
//
// If the configured day does not exist in a particular year,
// the occurrence uses the LAST VALID DAY of that month.
//
// Example:
//
//      February 29, every year
//
//      2024 → February 29
//      2025 → February 28
//      2026 → February 28
//      2027 → February 28
//      2028 → February 29
//
// ============================================================


// ============================================================
// LOCAL CALENDAR DATE HELPER
// ============================================================
//
// PostgreSQL DATE values represent calendar dates, not moments
// in time.
//
// If Supabase gives us:
//
//      2026-05-25T00:00:00.000Z
//
// we want:
//
//      May 25, 2026
//
// without timezone conversion moving the date.
//
// ============================================================

function toLocalCalendarDate(value) {

    if (!value) {
        return null
    }

    // --------------------------------------------------------
    // JavaScript Date supplied by Supabase/database.
    // --------------------------------------------------------

    if (value instanceof Date) {

        return new Date(
            value.getUTCFullYear(),
            value.getUTCMonth(),
            value.getUTCDate()
        )
    }

    // --------------------------------------------------------
    // Raw YYYY-MM-DD database value.
    // --------------------------------------------------------

    if (
        typeof value === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(value)
    ) {

        const [
            year,
            month,
            day
        ] =
            value
                .split('-')
                .map(Number)

        return new Date(
            year,
            month - 1,
            day
        )
    }

    // --------------------------------------------------------
    // Fallback for other date-like values.
    // --------------------------------------------------------

    const parsed =
        new Date(value)

    if (
        Number.isNaN(
            parsed.getTime()
        )
    ) {
        return null
    }

    return new Date(
        parsed.getUTCFullYear(),
        parsed.getUTCMonth(),
        parsed.getUTCDate()
    )
}


// ============================================================
// YEARLY RECURRENCE EVALUATOR
// ============================================================

export function evaluateYearly({

    template,

    operationalDate

}) {

    // ========================================================
    // START DATE
    // ========================================================

    const startDate =
        toLocalCalendarDate(
            template.recurrence_start_date
        )


    // ========================================================
    // CURRENT / OPERATIONAL DATE
    // ========================================================

    const currentDate =
        operationalDate instanceof Date

            ? new Date(
                operationalDate.getFullYear(),
                operationalDate.getMonth(),
                operationalDate.getDate()
            )

            : toLocalCalendarDate(
                operationalDate
            )


    // --------------------------------------------------------
    // Safety check
    // --------------------------------------------------------

    if (!currentDate) {

        return decision({

            generate: false,

            code:
                EngineResult.NOT_READY,

            reason:
                'Yearly recurrence has no valid operational date'

        })
    }


    // ========================================================
    // NOT STARTED
    // ========================================================

    if (
        startDate &&
        currentDate < startDate
    ) {

        return decision({

            generate: false,

            code:
                EngineResult.NOT_STARTED,

            reason:
                'Start date has not been reached'

        })
    }


    // ========================================================
    // END DATE
    // ========================================================

    const endDate =
        toLocalCalendarDate(
            template.recurrence_end_date
        )

    if (
        endDate &&
        currentDate > endDate
    ) {

        return decision({

            generate: false,

            code:
                EngineResult.EXPIRED,

            reason:
                'Template has expired'

        })
    }


    // ========================================================
    // DETERMINE MONTH
    // ========================================================
    //
    // month_of_year:
    //
    //      1 = January
    //      2 = February
    //      ...
    //      12 = December
    //
    // If it is not configured, use the month from the
    // recurrence start date.
    //
    // ========================================================

    const configuredMonth =
        Number(
            template.month_of_year
        )

    const month =
        Number.isInteger(
            configuredMonth
        ) &&
        configuredMonth >= 1 &&
        configuredMonth <= 12

            ? configuredMonth

            : startDate
                ? startDate.getMonth() + 1
                : null


    // ========================================================
    // DETERMINE DAY
    // ========================================================
    //
    // If day_of_month is not configured, use the day from the
    // recurrence start date.
    //
    // ========================================================

    const configuredDay =
        Number(
            template.day_of_month
        )

    const requestedDay =
        Number.isInteger(
            configuredDay
        ) &&
        configuredDay >= 1 &&
        configuredDay <= 31

            ? configuredDay

            : startDate
                ? startDate.getDate()
                : null


    // ========================================================
    // VALIDATE DATE CONFIGURATION
    // ========================================================

    if (
        !month ||
        !requestedDay
    ) {

        return decision({

            generate: false,

            code:
                EngineResult.NOT_READY,

            reason:
                'Yearly recurrence has no valid month or day'

        })
    }


    // ========================================================
    // DETERMINE ACTUAL DAY FOR THIS YEAR
    // ========================================================
    //
    // The configured day is the user's intended calendar day.
    //
    // If that day does not exist in the current year/month,
    // use the last valid day of that month.
    //
    // Example:
    //
    // February 29:
    //
    // leap year     → 29
    // non-leap year → 28
    //
    // ========================================================

    const daysInMonth =
        new Date(
            currentDate.getFullYear(),
            month,
            0
        ).getDate()

    const actualDay =
        Math.min(
            requestedDay,
            daysInMonth
        )


    // ========================================================
    // DOES TODAY MATCH THE MONTH?
    // ========================================================

    if (
        currentDate.getMonth() !==
        month - 1
    ) {

        return decision({

            generate: false,

            code:
                EngineResult.NOT_READY,

            reason:
                'Yearly recurrence month is not today'

        })
    }


    // ========================================================
    // DOES TODAY MATCH THE EFFECTIVE DAY?
    // ========================================================

    if (
        currentDate.getDate() !==
        actualDay
    ) {

        return decision({

            generate: false,

            code:
                EngineResult.NOT_READY,

            reason:
                'Yearly recurrence day is not today'

        })
    }


    // ========================================================
    // YEARLY INTERVAL
    // ========================================================
    //
    // NULL means every year.
    //
    // The interval is anchored to recurrence_start_date.
    //
    // Example:
    //
    // start year = 2026
    // interval   = 3
    //
    // valid years:
    //
    // 2026
    // 2029
    // 2032
    // 2035
    //
    // ========================================================

    const interval =
        Number(
            template.interval_value
        ) || 1


    if (
        !Number.isInteger(interval) ||
        interval < 1
    ) {

        return decision({

            generate: false,

            code:
                EngineResult.NOT_READY,

            reason:
                'Yearly recurrence interval is invalid'

        })
    }


    // ========================================================
    // DETERMINE ANCHOR YEAR
    // ========================================================

    const anchorYear =
        startDate
            ? startDate.getFullYear()
            : currentDate.getFullYear()


    // ========================================================
    // YEARS SINCE START
    // ========================================================

    const yearsSinceStart =
        currentDate.getFullYear() -
        anchorYear


    // --------------------------------------------------------
    // If the operational date somehow falls before the anchor
    // year, do not generate.
    // --------------------------------------------------------

    if (
        yearsSinceStart < 0
    ) {

        return decision({

            generate: false,

            code:
                EngineResult.NOT_READY,

            reason:
                'Yearly recurrence interval has not been reached'

        })
    }


    // ========================================================
    // CHECK YEARLY INTERVAL
    // ========================================================

    if (
        yearsSinceStart %
        interval !==
        0
    ) {

        return decision({

            generate: false,

            code:
                EngineResult.NOT_READY,

            reason:
                'Yearly recurrence interval not reached'

        })
    }


    // ========================================================
    // GENERATE TODAY'S OCCURRENCE
    // ========================================================

    return decision({

        generate: true,

        code:
            EngineResult.READY,

        reason:
            'Yearly recurrence',

        dueDate:
            currentDate

    })
}